import React, { useEffect, useState } from "react";
import {
  Container,
  Form,
  Button,
  Alert,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import axios from "axios";
import { useParams } from "react-router-dom";

const UpdateCustomer = () => {
  const { id } = useParams();
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const [formData, setFormData] = useState({
    company_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
    province: "",
    username: "",
    password: "",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (id) {
      const token = localStorage.getItem("admin_token");
      const headers = { Authorization: `Bearer ${token}` };

      axios
        .get(`${API_BASE}/api/customers_a/${id}`, { headers })
        .then((res) => {
          const { password_hash, ...rest } = res.data;
          setFormData({ ...rest, password: "" });
        })
        .catch(() => setError("Failed to load profile"));
    }
  }, [id, API_BASE]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isPasswordStrong = (password) =>
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[^A-Za-z0-9]/.test(password);

  const isValidCanadianPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, "");
    const regex = /^(?:\+1)?[2-9]\d{2}[2-9](?!11)\d{6}$/;
    return regex.test(cleaned);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const token = localStorage.getItem("admin_token");
    const headers = { Authorization: `Bearer ${token}` };
    const payload = { ...formData };

    if (!isValidCanadianPhone(payload.phone)) {
      setError("Please enter a valid Canadian phone number.");
      return;
    }

    if (!payload.password) {
      delete payload.password;
    } else if (!isPasswordStrong(payload.password)) {
      setError("Password must be at least 8 characters and include uppercase, lowercase, number, and special character.");
      return;
    }

    try {
      await axios.put(`${API_BASE}/api/customers_a/update/${id}`, payload, { headers });
      setSuccess("Profile updated successfully.");
    } catch (err) {
      if (err.response?.status === 409) {
        const msg = err.response.data?.error;
        if (msg === "username_exists") setError("Username already exists.");
        else if (msg === "email_exists") setError("Email already exists.");
        else setError("Username or email already exists.");
      } else {
        setError("Failed to update profile.");
      }
    }
  };

  return (
    <Container className="mt-5">
      <h3 className="text-center mb-4">Update Customer Profile</h3>

      <Card className="p-4 shadow rounded">
        {success && <Alert variant="success">{success}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Company Name</Form.Label>
                <Form.Control
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="e.g. 4165551234 or +14165551234"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>City</Form.Label>
                <Form.Control
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Province</Form.Label>
                <Form.Control
                  type="text"
                  name="province"
                  value={formData.province}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Postal Code</Form.Label>
                <Form.Control
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <div className="d-flex align-items-center">
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Leave blank to keep current password"
                    className="me-2"
                  />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </Button>
                </div>
                {formData.password && !isPasswordStrong(formData.password) && (
                  <Form.Text className="text-danger d-block">
                    Password should be at least 8 characters and include uppercase, lowercase, number, and symbol.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          <div className="text-center">
            <Button type="submit" variant="primary">
              Update Customer
            </Button>
          </div>
        </Form>
      </Card>
    </Container>
  );
};

export default UpdateCustomer;
